/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * A draft remote_session_issuer returned by discover. Same shape as RemoteSessionIssuer minus id/project_id/timestamps, plus discovery_warnings describing any RFC 8414 deviations.
 */
export type RemoteSessionIssuerDraft = {
  /**
   * Upstream authorization endpoint.
   */
  authorizationEndpoint?: string | undefined;
  /**
   * Advertised grant profiles; metadata evidence is not client authorization or user access.
   */
  authorizationGrantProfilesSupported?: Array<string> | undefined;
  /**
   * Whether the discovery document advertises the RFC 9207 iss parameter in authorization responses; false when the document omits the field.
   */
  authorizationResponseIssParameterSupported: boolean;
  /**
   * Whether the discovery document advertises OpenID Connect Back-Channel Logout support; false when the document omits the field.
   */
  backchannelLogoutSupported: boolean;
  /**
   * Claims the issuer can return in ID tokens and from userinfo. Null when the document omits the field.
   */
  claimsSupported?: Array<string> | null | undefined;
  /**
   * Whether the issuer advertises support for a Client ID Metadata Document URL as client_id (OAuth CIMD draft), parsed from the discovery document.
   */
  clientIdMetadataDocumentSupported: boolean;
  /**
   * PKCE code challenge methods advertised in the discovery document (RFC 8414 code_challenge_methods_supported). Null when the document omits the field.
   */
  codeChallengeMethodsSupported?: Array<string> | null | undefined;
  /**
   * Warnings describing any RFC 8414 deviations encountered during discovery.
   */
  discoveryWarnings: Array<string>;
  grantTypesSupported?: Array<string> | undefined;
  /**
   * JWS algorithms the issuer signs ID tokens with. Null when the document omits the field.
   */
  idTokenSigningAlgValuesSupported?: Array<string> | null | undefined;
  /**
   * RFC 7662 token introspection endpoint advertised in the discovery document. Null when not advertised.
   */
  introspectionEndpoint?: string | undefined;
  /**
   * Client authentication methods the introspection endpoint accepts. Null when the document omits the field.
   */
  introspectionEndpointAuthMethodsSupported?: Array<string> | null | undefined;
  /**
   * Issuer URL; matches the iss claim.
   */
  issuer: string;
  /**
   * Upstream JWKS URI; null when not advertised.
   */
  jwksUri?: string | undefined;
  /**
   * When true, may unlock OIDC-aware behaviour.
   */
  oidc: boolean;
  /**
   * RFC 8414 op_policy_uri; the issuer's client data-usage policy. Null when not advertised or when the advertised value is not an absolute http(s) URL.
   */
  opPolicyUri?: string | undefined;
  /**
   * RFC 8414 op_tos_uri; the issuer's terms of service. Null when not advertised or when the advertised value is not an absolute http(s) URL.
   */
  opTosUri?: string | undefined;
  /**
   * When true, the MCP client registers and transacts directly with this issuer.
   */
  passthrough: boolean;
  /**
   * Upstream RFC 7591 registration endpoint; null for issuers without DCR.
   */
  registrationEndpoint?: string | undefined;
  /**
   * Whether the issuer accepts the RFC 8707 resource parameter. Never proposed by discovery, so always null on a draft.
   */
  resourceIndicatorSupported?: boolean | undefined;
  responseTypesSupported?: Array<string> | undefined;
  /**
   * Upstream RFC 7009 revocation endpoint; null when the issuer advertises none.
   */
  revocationEndpoint?: string | undefined;
  /**
   * Operator-pinned scope request. Never proposed by discovery, so always null on a draft.
   */
  scopeOverride?: Array<string> | null | undefined;
  scopesSupported?: Array<string> | undefined;
  /**
   * RFC 8414 service_documentation; developer documentation for the issuer. Null when not advertised or when the advertised value is not an absolute http(s) URL.
   */
  serviceDocumentation?: string | undefined;
  /**
   * Upstream token endpoint.
   */
  tokenEndpoint?: string | undefined;
  tokenEndpointAuthMethodsSupported?: Array<string> | undefined;
  /**
   * OpenID Connect userinfo endpoint advertised in the discovery document. Null when not advertised.
   */
  userinfoEndpoint?: string | undefined;
};

/** @internal */
export const RemoteSessionIssuerDraft$inboundSchema: z.ZodMiniType<
  RemoteSessionIssuerDraft,
  unknown
> = z.pipe(
  z.object({
    authorization_endpoint: z.optional(z.string()),
    authorization_grant_profiles_supported: z.optional(z.array(z.string())),
    authorization_response_iss_parameter_supported: z.boolean(),
    backchannel_logout_supported: z.boolean(),
    claims_supported: z.optional(z.nullable(z.array(z.string()))),
    client_id_metadata_document_supported: z.boolean(),
    code_challenge_methods_supported: z.optional(
      z.nullable(z.array(z.string())),
    ),
    discovery_warnings: z.array(z.string()),
    grant_types_supported: z.optional(z.array(z.string())),
    id_token_signing_alg_values_supported: z.optional(
      z.nullable(z.array(z.string())),
    ),
    introspection_endpoint: z.optional(z.string()),
    introspection_endpoint_auth_methods_supported: z.optional(
      z.nullable(z.array(z.string())),
    ),
    issuer: z.string(),
    jwks_uri: z.optional(z.string()),
    oidc: z.boolean(),
    op_policy_uri: z.optional(z.string()),
    op_tos_uri: z.optional(z.string()),
    passthrough: z.boolean(),
    registration_endpoint: z.optional(z.string()),
    resource_indicator_supported: z.optional(z.boolean()),
    response_types_supported: z.optional(z.array(z.string())),
    revocation_endpoint: z.optional(z.string()),
    scope_override: z.optional(z.nullable(z.array(z.string()))),
    scopes_supported: z.optional(z.array(z.string())),
    service_documentation: z.optional(z.string()),
    token_endpoint: z.optional(z.string()),
    token_endpoint_auth_methods_supported: z.optional(z.array(z.string())),
    userinfo_endpoint: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "authorization_endpoint": "authorizationEndpoint",
      "authorization_grant_profiles_supported":
        "authorizationGrantProfilesSupported",
      "authorization_response_iss_parameter_supported":
        "authorizationResponseIssParameterSupported",
      "backchannel_logout_supported": "backchannelLogoutSupported",
      "claims_supported": "claimsSupported",
      "client_id_metadata_document_supported":
        "clientIdMetadataDocumentSupported",
      "code_challenge_methods_supported": "codeChallengeMethodsSupported",
      "discovery_warnings": "discoveryWarnings",
      "grant_types_supported": "grantTypesSupported",
      "id_token_signing_alg_values_supported":
        "idTokenSigningAlgValuesSupported",
      "introspection_endpoint": "introspectionEndpoint",
      "introspection_endpoint_auth_methods_supported":
        "introspectionEndpointAuthMethodsSupported",
      "jwks_uri": "jwksUri",
      "op_policy_uri": "opPolicyUri",
      "op_tos_uri": "opTosUri",
      "registration_endpoint": "registrationEndpoint",
      "resource_indicator_supported": "resourceIndicatorSupported",
      "response_types_supported": "responseTypesSupported",
      "revocation_endpoint": "revocationEndpoint",
      "scope_override": "scopeOverride",
      "scopes_supported": "scopesSupported",
      "service_documentation": "serviceDocumentation",
      "token_endpoint": "tokenEndpoint",
      "token_endpoint_auth_methods_supported":
        "tokenEndpointAuthMethodsSupported",
      "userinfo_endpoint": "userinfoEndpoint",
    });
  }),
);

export function remoteSessionIssuerDraftFromJSON(
  jsonString: string,
): SafeParseResult<RemoteSessionIssuerDraft, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RemoteSessionIssuerDraft$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RemoteSessionIssuerDraft' from JSON`,
  );
}
